import { birthdayValidate } from "@/lib/birthdayValidate";
import calcAge from "@/lib/calcAge";
import ChatGuideLineAgreement from "@/lib/chatGuideLineAgreement";
import {
  errorMessages,
  GENDER,
  idcNoCooperatedErrorCode,
  logicaAlreadyCooperatedErrorCode,
  logicaSignupDuplicateCaseInsensitiveErrorCode,
  logicaSignupDuplicateErrorCode,
  logicaSignupRestrictionAgeErrorCode,
  metaSchemas,
  SIGNUP_RESTRICTION_AGE,
  SIGNUP_RESTRICTION_AGE_MESSAGE,
  userRating,
} from "@/lib/constants";
import dayjs from "@/lib/day";
import { AUTH_ERROR_MESSAGES } from "@/lib/errors";
import getRemoteIpAddress from "@/lib/getRemoteIpAddress";
import IdcToken from "@/lib/idcToken";
import {
  passwordResetComplete,
  passwordResetTokenMail,
  passwordUpdateComplete,
  profilePinResetTokenMail,
  signupComplete,
  userUpdate,
  withdrawalComplete,
} from "@/lib/mailTemplate";
import { isEnded } from "@/lib/metaUtil";
import PasswordToken from "@/lib/passwordToken";
import PINCode from "@/lib/pinCode";
import sendEmail from "@/lib/sendEmail";
import Uid2Token from "@/lib/uid2Token";
import UserToken from "@/lib/userToken";
import { passwordFormatValidate } from "@/lib/yupPasswordValidate";
import { gql } from "@apollo/client";
import { composeResolvers } from "@graphql-tools/resolvers-composition";
import { UserInputError } from "apollo-server-errors";
import {
  convertIdcGenderToSpooxSex,
  createPageInfo,
  createToken,
  isAuthenticated,
  logicaErrorCode,
  metaResolveType,
  ownerOnly,
  sleep,
  values,
} from "./helper";

function passwordValidate(password) {
  return password && password.length >= 8 && passwordFormatValidate(password);
}

function ageValidate(value) {
  const age = calcAge(value);
  if (age < SIGNUP_RESTRICTION_AGE) {
    throw new UserInputError(SIGNUP_RESTRICTION_AGE_MESSAGE);
  }
}

export const typeDef = gql`
  interface User {
    uuid: ID!
    id: Int
    display_name: String
    birthday: String
    sex: Int
    type: String
  }

  type AccountUser implements User @cacheControl(scope: PRIVATE, maxAge: 0) {
    uuid: ID!
    id: Int
    display_name: String
    birthday: String
    age: Int
    sex: Int
    type: String
    mail_address: String
    first_name: String
    last_name: String
    fullName: String
    rights_free_flag: Boolean
    currentProfile: ProfileUser
    profiles: [ProfileUser]
    test_flg: Boolean
    hashedUserId: String
    receiveAdvertisingMailType: Int
  }

  union FavoriteMeta = EpisodeMeta | LiveMeta | TVShowMeta | ChannelMeta

  type UserFavorites @cacheControl(maxAge: 0) {
    id: Int
    metas: [FavoriteMeta]
  }

  type ProfileUser implements User @cacheControl(scope: PRIVATE, maxAge: 0) {
    uuid: ID!
    id: Int
    display_name: String
    birthday: String
    age: Int
    sex: Int
    type: String
    passcode: String
    ownerProfile: Boolean
    needPasscode: Boolean
    icon: Int
    rating: Int
  }

  type Auth {
    token: String
    user: AccountUser
    error: IdcTokenAuthError
  }

  type Login {
    token: String
    error: IdcTokenAuthError
  }

  type MappingResponce {
    user: AccountUser
    error: IdcTokenAuthError
  }

  type IdcTokenAuthError {
    code: String
  }

  type UserUpdate {
    user: AccountUser
    error: String
  }

  type Profile @cacheControl(scope: PRIVATE, maxAge: 0) {
    profile: ProfileUser
  }

  type AuthResult @cacheControl(scope: PRIVATE, maxAge: 0) {
    result: Boolean
  }

  type UserConnection {
    user: AccountUser
    profiles: [ProfileUser]
  }

  type PinCodeResult {
    result: Boolean
  }

  type VerifyResult {
    verified: Boolean
    error: String
  }

  type FavoriteResult @cacheControl(maxAge: 0) {
    result: Boolean
    favorites: UserFavorites
  }

  type FavoritesByContentTypeResult @cacheControl(maxAge: 0) {
    data: [FavoriteMeta]
    pageInfo: PageInfo
  }

  type ViewingHistoriesResult @cacheControl(scope: PRIVATE, maxAge: 0) {
    data: [MediaMeta]
    pageInfo: PageInfo
  }

  type AddResumeResult {
    result: Boolean
  }

  type UserDeleteResponse @cacheControl(scope: PRIVATE) {
    result: Boolean
  }
  type AuthOwnerProfileResponse @cacheControl(scope: PRIVATE) {
    result: Boolean
  }

  type DeleteProfileResponse @cacheControl(scope: PRIVATE) {
    result: Boolean
  }

  type DeleteViewingHistoryResult @cacheControl(scope: PRIVATE, maxAge: 0) {
    result: Boolean
  }

  # type BroadcastDiscountResult @cacheControl(scope: PRIVATE, maxAge: 0) {
  #   result: Boolean
  #   user_voucher_id: Int
  # }

  type AdvertisingPreferencesResult @cacheControl(scope: PRIVATE, maxAge: 0) {
    request_id: String
  }

  type Query {
    user: AccountUser

    userIsLogin: AccountUser

    userFavorites: UserFavorites

    favoritesByContentType(
      type: String
      offset: Int
      limit: Int
    ): FavoritesByContentTypeResult

    viewingHistories(
      offset: Int
      limit: Int
      sort: String
      nocache: Boolean
    ): ViewingHistoriesResult

    # broadcastDiscount(courseId: Int, productId: Int): BroadcastDiscountResult

    checkTokenTimeout: AuthResult
  }

  type Mutation {
    login(loginid: String!, password: String!, signature: String!): Auth

    logout(force: Boolean): AuthResult

    selectProfile(profileId: Int, passcode: String, type: String): AccountUser

    sendProfilePinResetToken: PinCodeResult

    sendPasswordResetToken(mailAddress: String!): PinCodeResult

    verifyPinCode(code: String!, mailAddress: String!): VerifyResult

    verifyPasswordToken(token: String!): VerifyResult

    signup(
      code: String!
      mailAddress: String!
      password: String!
      firstName: String!
      lastName: String!
      birthday: String!
      sex: String!
      terms: String!
      signature: String!
    ): Auth

    addFavorite(id: Int!): FavoriteResult

    deleteFavorite(id: Int!): FavoriteResult

    addResume(
      resume: Int
      metaId: Int
      groupedMetaId: Int
      psid: String
      reachToEnd: Boolean
    ): AddResumeResult

    update(mailAddress: String, code: String): UserUpdate

    updatePassword(currentPassword: String, password: String): UserUpdate

    updateOwnerProfile(
      displayName: String
      icon: Int
      passcode: String
      rating: Int
    ): UserUpdate

    passwordReset(password: String!, token: String!): PinCodeResult

    passcodeReset(passcode: String!, token: String!): PinCodeResult

    addProfile(
      displayName: String
      birthday: String
      sex: String
      icon: Int
      rating: Int
      passcode: String
    ): Profile

    updateProfile(
      displayName: String
      birthday: String
      sex: String
      password: String
      profileId: Int
      icon: Int
      rating: Int
      passcode: String
    ): Profile

    deleteProfile(profileId: Int): DeleteProfileResponse

    userDelete: UserDeleteResponse

    authProfile(profileId: Int, passcode: String): AuthOwnerProfileResponse

    deleteViewingHistory(metaIds: [String]): DeleteViewingHistoryResult

    advertisingPreferences(
      receiveAdvertisingMailFlag: Boolean
    ): AdvertisingPreferencesResult

    refreshToken: AuthResult

    idcTokenLogin(signature: String!): Auth

    idcTokenSignup(password: String!, terms: String!, signature: String!): Auth

    idcTokenMapping(signature: String!): MappingResponce
  }
`;

async function getProfiles(parent, dataSources) {
  const profiles = await (async () => {
    if (!parent?.profiles) {
      const res = await dataSources.user.me();
      return [res.user, ...res?.profiles];
    } else {
      const profileIds = parent.profiles.map((p) => p.id);
      if (profileIds.includes(parent.id)) {
        return [...parent.profiles];
      } else {
        return [parent, ...parent.profiles];
      }
    }
  })();
  return profiles;
}

function getFavoriteMetas(dataSources) {
  try {
    const res = dataSources.user.allFavorites({
      offset: 0,
      limit: 50,
    });
    return res;
  } catch (e) {
    console.log("getFavoriteMetas", e);
    return null;
  }
}

async function deleteToken(req, res) {
  const userToken = new UserToken(req, res);
  await userToken.delete();
  const chatGuideLineAgreement = new ChatGuideLineAgreement(req, res);
  await chatGuideLineAgreement.delete();
  const uid2 = new Uid2Token(req, res);
  uid2.delete();
  req.userToken = null;
  const idcToken = new IdcToken(req, res);
  await idcToken.delete();
}

export const resolvers = composeResolvers(
  {
    Query: {
      user: async (_parent, _args, { req, res, dataSources }) => {
        if (req.userToken) {
          try {
            const result = await dataSources.user.me();
            const { user } = result;

            user.profiles = result.profiles;
            // hashedUserIdがなかったらユーザートークンを更新する
            // レコメンド対応で必要だが、リリース時にログインしていたユーザーのuserTokenからは取得できないため
            if (!req.userToken.hashedUserId) {
              const userToken = new UserToken(req, res);
              await userToken.refresh({
                hashedUserId: user?.values?.user_id_hash,
              });
            }
            return user;
          } catch (e) {
            console.log(e);
            await deleteToken(req, res);
            return null;
          }
        }
        return null;
      },
      userFavorites: async (_parent, _args, { req, dataSources }) => {
        if (req.userToken) {
          try {
            const favoriteMetas = await getFavoriteMetas(dataSources);
            const id = req.userToken.currentProfile || req.userToken.id;
            return { id, metas: favoriteMetas };
          } catch (e) {
            return { metas: [] };
          }
        }
      },
      // お気に入り取得
      favoritesByContentType: async (_parent, args, { req, dataSources }) => {
        const { type, offset, limit } = args;
        const metaSchemaId = (() => {
          if (type === "channel") {
            return metaSchemas.channel.id;
          } else if (type === "tvshow") {
            return metaSchemas.tvshow.id;
          } else {
            return metaSchemas.episode.id;
          }
        })();
        const dataLimit = 200;

        const res = await dataSources.user.favoritesByMetaSchemaId({
          offset: 0,
          limit: dataLimit,
          metaSchemaId: metaSchemaId,
        });
        let favoriteMetas = res.user_favorites
          .map((fav) => fav.meta)
          .filter((meta) => !!meta);

        const now = dayjs();
        const deliveryDateFilter = (meta) => {
          if (meta.delivery_end_at) {
            return now.isBefore(meta.delivery_end_at);
          }
          return true;
        };

        if (type === "vod") {
          favoriteMetas = favoriteMetas
            .filter((meta) => {
              return !meta?.values?.is_live;
            })
            .filter(deliveryDateFilter);
        } else if (type === "catchup") {
          favoriteMetas = favoriteMetas
            .filter((meta) => {
              if (!meta?.values?.is_live) return false;
              if (!meta?.values?.catch_up_flag) return false;
              const encodeEnded = isEnded(meta?.values?.encode_end_date);
              if (!encodeEnded) return false;
              const deliveryEnded = isEnded(meta?.delivery_end_at);
              if (deliveryEnded) return false;
              return true;
            })
            .filter(deliveryDateFilter);
        } else if (type === "live") {
          favoriteMetas = favoriteMetas
            .filter((meta) => {
              if (!meta?.values?.is_live) {
                return false;
              }
              const ended = isEnded(meta?.values?.encode_end_date);
              return !ended;
            })
            .filter(deliveryDateFilter);
        } else if (type === "episode") {
          //  VODと見逃しのみ表示
          favoriteMetas = favoriteMetas
            .filter((meta) => {
              if (!meta?.values?.is_live) return true;
              const ended = isEnded(meta?.values?.encode_end_date);
              return meta?.values?.catch_up_flag && ended;
            })
            .filter(deliveryDateFilter);
        }

        favoriteMetas = favoriteMetas.filter((meta) => {
          const rating = req.userToken.rating;
          const permitRating = userRating[rating].permitRating;
          return permitRating.includes(meta.values.rating);
        });

        const totalCount = favoriteMetas.length;
        favoriteMetas = favoriteMetas.slice(offset, offset + limit);

        return {
          data: favoriteMetas,
          pageInfo: createPageInfo({ limit, offset, count: totalCount }),
        };
      },
      viewingHistories: async (
        _parent,
        { offset, limit, sort, nocache },
        { dataSources }
      ) => {
        try {
          const res = await dataSources.user.getViewingHistory({
            offset,
            limit,
            sort,
            nocache,
          });
          let metas = res.viewing_histories.map((vh) => {
            return {
              ...vh.meta,
              resume_point: vh.resume_point,
              last_viewing_at: vh.last_viewing_at,
            };
          });

          return {
            data: metas,
            pageInfo: createPageInfo({ limit, offset, count: res.total_count }),
          };
        } catch (e) {
          console.log("getViewingHistory error", e);
        }
      },
      // broadcastDiscount: async (_parent, args, { dataSources }) => {
      //   try {
      //     const res = await dataSources.user.broadcastDiscount(args);

      //     console.log("broadcastDiscount", res);
      //     return res;
      //   } catch (e) {
      //     console.log("delete broadcastDiscount error", e);
      //   }
      // },
      checkTokenTimeout: async (_parent, _args, { req, res }) => {
        try {
          const userToken = new UserToken(req, res);
          const result = await userToken.checkExpiration();
          return { result: result };
        } catch (e) {
          console.log("checkTokenTimeout error", e);
          return { result: false };
        }
      },
    },
    Mutation: {
      // ログイン
      login: async (
        _parent,
        { loginid, password, signature },
        { req, res, dataSources }
      ) => {
        try {
          console.log("login idcUserInfo request");
          try {
            const idcUserInfo = await dataSources.idc.userInfoEndPoint();
            console.log(idcUserInfo);
            const birthday = idcUserInfo.birthdate?.replace(
              new RegExp("/", "g"),
              "-"
            );
            ageValidate(birthday);
          } catch (e) {
            console.log("idcUserInfo error", e);
            return e;
          }
          const user = await dataSources.user.login(loginid, password);

          console.log("login res user", user);
          const token = await createToken(req, res, user, signature);
          return {
            token,
            user: user.user,
          };
        } catch (e) {
          console.log("login auth error", e);
          // console.log(JSON.stringify(e.extentions, null, 2));
          const response = e?.extensions?.response?.body;
          console.log("login auth error", response);
          if (response && response?.error?.code == 10006) {
            throw new UserInputError(AUTH_ERROR_MESSAGES["acountlock"]);
          } else if (
            response &&
            response?.error?.code == idcNoCooperatedErrorCode
          ) {
            // 未連携エラー
            return {
              error: { code: response.error.code },
            };
          } else if (response && response?.error?.code == 10000) {
            throw new UserInputError(AUTH_ERROR_MESSAGES["notfound"]);
          } else {
            throw new UserInputError(AUTH_ERROR_MESSAGES["unauthorized"]);
          }
        }
      },
      // ログアウト
      logout: async (_parent, { force }, { req, res, dataSources }) => {
        try {
          const { result } = await dataSources.user.logout();
          if (result) {
            await deleteToken(req, res);
          }
          return { result };
        } catch (e) {
          const errorCode = logicaErrorCode(e);
          console.log(`logout error errorCode: ${errorCode}`);
          return { result: false };
        } finally {
          if (force) {
            await deleteToken(req, res);
          }
        }
      },
      // プロフィールを選択
      selectProfile: async (parent, args, { req, res, dataSources }) => {
        // args : profileId, passcode, type
        // type : auth, no-auth
        const userToken = new UserToken(req, res);
        const token = await userToken.verify();
        if (token) {
          const res = await dataSources.user.me();
          const profiles = await getProfiles(parent, dataSources);
          const { user } = res;
          const selectProfile = profiles.find(
            (profile) => profile.id == args.profileId
          );
          if (selectProfile) {
            if (
              args.type == "auth" &&
              selectProfile.values?.passcode &&
              selectProfile.values?.passcode != args.passcode
            ) {
              throw new UserInputError(AUTH_ERROR_MESSAGES["invalidPasscode"]);
            }
            user.profiles = profiles;
            user.selectProfileId = args.profileId;
            await userToken.refresh({
              currentProfile: args.profileId,
              rating: selectProfile.values?.rating,
            });
            return user;
          }
        }
        return null;
      },
      // プロフィール暗証番号リセットURLの送信
      sendProfilePinResetToken: async (
        _parent,
        _args,
        { req, res, dataSources }
      ) => {
        const { user } = await dataSources.user.me();
        const mailAddress = user.mail_address;
        const passwordToken = new PasswordToken(req, res);
        const token = await passwordToken.publish(mailAddress);
        await sendEmail(
          profilePinResetTokenMail({ address: mailAddress, token: token })
        );
        return { result: true };
      },
      // パスワードリセットメールの送信
      sendPasswordResetToken: async (
        _parent,
        { mailAddress },
        { req, res, dataSources }
      ) => {
        const user = await dataSources.user.existed(mailAddress);
        if (user) {
          // ユーザーが存在していたらTokenメールを送る
          const passwordToken = new PasswordToken(req, res);
          const token = await passwordToken.publish(mailAddress);
          await sendEmail(
            passwordResetTokenMail({ address: mailAddress, token: token })
          );
        }
        return { result: true };
      },
      // PINコード検証
      verifyPinCode: async (_parent, args, { req, res }) => {
        const pinCode = new PINCode(req, res);
        const verified = await pinCode.verify(args.code, args.mailAddress);
        if (!verified) {
          throw new UserInputError(AUTH_ERROR_MESSAGES["pincode"], {
            invalidArgs: Object.keys(args),
          });
        }
        return { verified: verified };
      },
      // passwordToken検証
      verifyPasswordToken: async (_parent, { token }, { req, res }) => {
        const passwordToken = new PasswordToken(req, res);
        const verified = await passwordToken.verify(token);
        if (!verified) {
          console.log(
            "verifyPasswordToken error",
            AUTH_ERROR_MESSAGES["passwordToken"]
          );
          // } else {
          //   await passwordToken.delete(token);
        }
        return { verified: verified };
      },
      // サインアップ
      signup: async (_parent, args, { req, res, dataSources }) => {
        try {
          console.log("############### signup mutation start ###############");
          // パスワードバリデート
          if (!passwordValidate(args.password)) {
            throw new UserInputError(errorMessages["badPassword"], {
              invalidArgs: ["password"],
            });
          }
          // 生年月日をバリデート
          if (!birthdayValidate(args?.birthday)) {
            throw new UserInputError(errorMessages["signup"]);
          }
          // ここでもメールアドレスが認証済みかの確認をする
          const pinCode = new PINCode(req, res);
          const verified = await pinCode.verify(args.code, args.mailAddress);
          if (!verified) {
            throw new UserInputError(AUTH_ERROR_MESSAGES["pincode"], {
              invalidArgs: Object.keys(args),
            });
          }
          // ユーザーがすでに存在しているか
          const existsUser = await dataSources.user.existed(args.mailAddress);
          if (existsUser) {
            // console.log("user existed email: ", args.mailAddress);
            throw new UserInputError(AUTH_ERROR_MESSAGES["signup"]);
          }

          const ua = req.headers["user-agent"];
          const clientIp = getRemoteIpAddress(req);

          console.log("ua: ", ua);
          console.log("ip: ", clientIp);
          console.log("signup api request");
          const user = await dataSources.user.signup(args);

          if (user) {
            console.log("user registered");
            console.log("userId: ", user.id);
          }
          pinCode.delete(args.mailAddress);
          const token = await createToken(
            req,
            res,
            user,
            args.signature,
            false
          );

          console.log("agreedterms: ", args.terms);
          // 規約の同意
          const agreeTerms = args.terms.split(",");
          await Promise.all(
            agreeTerms.map(async (key) => {
              return await dataSources.terms.agree(key);
            })
          );
          await sendEmail(
            signupComplete({ address: args.mailAddress, user: user.user })
          );
          const uid2 = new Uid2Token(req, res);
          await uid2.getToken();
          console.log("############### signup mutation end ###############");
          return { token, user: user.user };
        } catch (e) {
          console.log("signup error", e);
          if (e instanceof UserInputError) {
            throw e;
          } else {
            throw new UserInputError(AUTH_ERROR_MESSAGES["signup"]);
          }
        }
      },
      // お気に入り追加
      addFavorite: async (_parent, { id }, { req, dataSources }) => {
        const { result } = await dataSources.user.addFavorite(id);
        await sleep(200);
        const favoriteMetas = await getFavoriteMetas(dataSources);
        const userId = req.userToken.currentProfile || req.userToken.id;
        return {
          result,
          favorites: { id: userId, metas: favoriteMetas },
        };
      },
      // お気に入り削除
      deleteFavorite: async (_parent, { id }, { req, dataSources }) => {
        const { result } = await dataSources.user.deleteFavorite(id);
        await sleep(200);
        const favoriteMetas = await getFavoriteMetas(dataSources);
        const userId = req.userToken.currentProfile || req.userToken.id;
        return {
          result,
          favorites: { id: userId, metas: favoriteMetas },
        };
      },
      // 視聴履歴、ブックマーク追加
      addResume: async (_parent, args, { dataSources }) => {
        try {
          await dataSources.user.addViewingHistory(args);
          await dataSources.user.addViewingBookmark(args);
          return { result: true };
        } catch (e) {
          console.log("addResume error", JSON.stringify(e, null, 2));
          return { result: false };
        }
      },
      update: async (
        _parent,
        { mailAddress, code },
        { req, res, dataSources }
      ) => {
        try {
          // メールアドレスが認証済みかの確認をする
          const pinCode = new PINCode(req, res);
          const verified = await pinCode.verify(code, mailAddress);
          if (!verified) {
            throw new UserInputError(errorMessages["pincode"], {
              invalidArgs: Object.keys({ mailAddress, code }),
            });
          }
          const user = await dataSources.user.update({ mailAddress });
          pinCode.delete(mailAddress);
          await sendEmail(
            userUpdate({ address: user?.user?.mail_address, user: user.user })
          );
          return { user: user.user };
        } catch (e) {
          console.log("update error", e);
          if (e instanceof UserInputError) {
            throw e;
          } else {
            throw new UserInputError(errorMessages["update"]);
          }
        }
      },
      updatePassword: async (
        _parent,
        { currentPassword, password },
        { req, dataSources }
      ) => {
        try {
          // パスワード確認
          const check = await dataSources.user.checkPassword(currentPassword);
          if (check.result) {
            // パスワードバリデート
            if (!passwordValidate(password)) {
              throw new UserInputError(errorMessages["badPassword"], {
                invalidArgs: ["password"],
              });
            }
            const user = await dataSources.user.updatePassword(password);
            const mailAddress = user?.user?.mail_address;
            await sendEmail(passwordUpdateComplete({ address: mailAddress }));
            return { user: user.user };
          } else {
            throw new UserInputError(errorMessages["invalidPassword"]);
          }
        } catch (e) {
          console.log("update password error", e);
          if (e instanceof UserInputError) {
            throw e;
          } else {
            throw new UserInputError(errorMessages["updatePassword"]);
          }
        }
      },
      passwordReset: async (
        _parent,
        { token, password },
        { req, res, dataSources }
      ) => {
        try {
          const passwordToken = new PasswordToken(req, res);
          const mailAddress = await passwordToken.getValue(token);
          const user = await dataSources.user.existed(mailAddress);
          const id = user?.id;
          const result = await dataSources.user.passwordUpdate({
            userId: id,
            password,
          });
          await passwordToken.delete(token);
          if (result) {
            await sendEmail(passwordResetComplete({ address: mailAddress }));
            return { result: true };
          } else {
            return { result: false };
          }
        } catch (e) {
          console.log("password reset error", e);
          if (e?.extensions?.response) {
            console.dir(e.extensions.response);
          }
          throw new UserInputError(errorMessages["passwordReset"]);
        }
      },
      passcodeReset: async (
        _parent,
        { token, passcode },
        { req, res, dataSources }
      ) => {
        try {
          // パスコードをバリデート
          if (passcode && !/^\d{4}$/.test(passcode)) {
            throw new UserInputError(errorMessages["passcodeReset"]);
          }
          const passwordToken = new PasswordToken(req, res);
          const mailAddress = await passwordToken.getValue(token);
          const { user } = await dataSources.user.me();
          await passwordToken.delete(token);
          if (user.mail_address === mailAddress) {
            await dataSources.user.updateOwnerProfile({
              passcode,
            });
            const userToken = new UserToken(req, res);
            await userToken.refresh();
            return { result: true };
          } else {
            console.log("update passcode reset mailAddress unmatched.");
            throw new Error("mailAddress unmatched.");
          }
        } catch (e) {
          console.log("update owner passcode reset error", e);
          throw new UserInputError(errorMessages["passcodeReset"]);
        }
      },
      updateOwnerProfile: async (_parent, args, { req, res, dataSources }) => {
        try {
          // パスコードをバリデート
          if (args.passcode && !/^\d{4}$/.test(args.passcode)) {
            throw new UserInputError(errorMessages["profileUpdate"]);
          }
          const user = await dataSources.user.updateOwnerProfile(args);
          const userToken = new UserToken(req, res);
          await userToken.refresh({
            rating: args.rating,
          });
          return { user: user.user };
        } catch (e) {
          console.log("update owner profile error", e);
          throw new UserInputError(errorMessages["profileUpdate"]);
        }
      },
      addProfile: async (_parent, args, { dataSources }) => {
        try {
          // パスコードをバリデート
          if (args.passcode && !/^\d{4}$/.test(args.passcode)) {
            throw new UserInputError(errorMessages["profileUpdate"]);
          }
          // 生年月日をバリデート
          if (!birthdayValidate(args?.birthday)) {
            throw new UserInputError(errorMessages["profileUpdate"]);
          }
          const result = await dataSources.user.addProfile(args);
          // 暫定対応: profiles系のAPIの時だけActiveRecord::Modelがそのままjsonになっているため
          // 性別が文字列で返ってきてしまう
          if (typeof result?.profile?.sex === "string") {
            result.profile.sex = GENDER[result.profile.sex];
          }
          return { profile: result.profile };
        } catch (e) {
          console.log("add profile error", e);
          throw new UserInputError(errorMessages["profile"]);
        }
      },
      updateProfile: async (_parent, args, { dataSources }) => {
        try {
          // パスコードをバリデート
          if (args.passcode && !/^\d{4}$/.test(args.passcode)) {
            throw new UserInputError(errorMessages["profileUpdate"]);
          }
          // 生年月日をバリデート
          if (!birthdayValidate(args?.birthday)) {
            throw new UserInputError(errorMessages["profileUpdate"]);
          }
          const result = await dataSources.user.updateProfile(args);
          // 暫定対応: profiles系のAPIの時だけActiveRecord::Modelがそのままjsonになっているため
          // 性別が文字列で返ってきてしまう
          if (typeof result?.profile?.sex === "string") {
            result.profile.sex = GENDER[result.profile.sex];
          }
          return { profile: result.profile };
        } catch (e) {
          console.log("update profile error", e);
          throw new UserInputError(errorMessages["profileUpdate"]);
        }
      },
      deleteProfile: async (_parent, args, { dataSources }) => {
        try {
          const profile = await dataSources.user.deleteProfile(args);
          return profile;
        } catch (e) {
          console.log("delete profile error", e);
          throw new UserInputError(errorMessages["profileDelete"]);
        }
      },
      userDelete: async (_parent, _args, { req, res, dataSources }) => {
        try {
          const { user } = await dataSources.user.me();

          const { result } = await dataSources.user.withdrawal();
          const userToken = new UserToken(req, res);
          await userToken.deleteAll();
          req.userToken = null;
          if (result) {
            await sendEmail(
              withdrawalComplete({ address: user?.mail_address, user: user })
            );
          }
          return { result };
        } catch (e) {
          console.log("user delete error", e);
          throw new UserInputError(errorMessages["userDelete"]);
        }
      },
      authProfile: async (parent, args, { req, dataSources }) => {
        try {
          const profiles = await getProfiles(parent, dataSources);
          const profile = profiles.find(
            (profile) => profile.id == args.profileId
          );
          const passcode = profile?.values?.passcode;
          if (passcode == args.passcode) {
            return { result: true };
          } else {
            return { result: false };
          }
        } catch (e) {
          console.log("authOwnerProfile error", e);
        }
      },
      deleteViewingHistory: async (_parent, { metaIds }, { dataSources }) => {
        try {
          const res = await dataSources.user.deleteViewingHistory(
            metaIds.join(",")
          );
          await sleep(200);
          return res;
        } catch (e) {
          console.log("delete ViewingHistory error", e);
          // throw new UserInputError(errorMessages["deleteViewingHistory"]);
        }
      },
      advertisingPreferences: async (
        _parent,
        { receiveAdvertisingMailFlag },
        { dataSources }
      ) => {
        try {
          const res = await dataSources.user.advertisingPreferences({
            receiveAdvertisingMailFlag,
          });
          return res;
        } catch (e) {
          console.log("advertisingPreferences error", e);
          throw new UserInputError(errorMessages["advertisingPreferences"]);
        }
      },
      refreshToken: async (_parent, _args, { req, res }) => {
        try {
          const userToken = new UserToken(req, res);
          const token = await userToken.refresh();
          if (token) {
            return { result: true };
          } else {
            return { result: false };
          }
        } catch (e) {
          console.log("refreshToken error", e);
          return { result: false };
        }
      },
      idcTokenLogin: async (
        _parent,
        { signature },
        { req, res, dataSources }
      ) => {
        try {
          const idcToken = new IdcToken(req, res);
          const data = await idcToken.verify();
          const user = await dataSources.user.idcTokenLogin(
            data.payload.accessToken,
            data.payload.refreshToken
          );

          const token = await createToken(req, res, user, signature, false);
          const uid2 = new Uid2Token(req, res);
          await uid2.getToken();
          return {
            token,
            user: user.user,
          };
        } catch (e) {
          console.log("idctokenlogin auth error", e);
          // console.log(JSON.stringify(e.extentions, null, 2));
          const response = e?.extensions?.response?.body;
          console.log("idctokenlogin auth error", response);
          if (response && response?.error?.code == 10006) {
            throw new UserInputError(AUTH_ERROR_MESSAGES["acountlock"]);
          } else if (
            response &&
            response?.error?.code == idcNoCooperatedErrorCode
          ) {
            // 未連携エラー or 未ログイン
            return {
              error: { code: response.error.code },
            };
          } else {
            throw new UserInputError(AUTH_ERROR_MESSAGES["default"]);
          }
        }
      },
      // 統合会員基盤対応サインアップ
      idcTokenSignup: async (_parent, args, { req, res, dataSources }) => {
        try {
          console.log(
            "############### idcTokenSignup mutation start ###############"
          );

          const ua = req.headers["user-agent"];
          const clientIp = getRemoteIpAddress(req);

          console.log("ua: ", ua);
          console.log("ip: ", clientIp);

          console.log("signup idcUserInfo request");
          try {
            const idcUserInfo = await dataSources.idc.userInfoEndPoint();
            args.mailAddress = idcUserInfo.email;
            args.firstName = "";
            args.lastName = idcUserInfo.name;
            args.firstNameKana = "";
            args.lastNameKana = idcUserInfo.namekana;
            args.sex = convertIdcGenderToSpooxSex(idcUserInfo.gender);
            args.birthday = idcUserInfo.birthdate?.replace(
              new RegExp("/", "g"),
              "-"
            );
            ageValidate(args.birthday);
          } catch (e) {
            console.log("idcUserInfo error", e);
            return e;
          }
          console.log("signup api request");

          const idcToken = new IdcToken(req, res);
          const data = await idcToken.verify();
          args.accessToken = data.payload.accessToken;
          args.refreshToken = data.payload.refreshToken;
          const user = await dataSources.user.idcTokenSignup(args);

          if (user) {
            console.log("user registered");
            console.log("userId: ", user.id);
          }
          const token = await createToken(
            req,
            res,
            user,
            args.signature,
            false
          );

          console.log("agreedterms: ", args.terms);
          // 規約の同意
          const agreeTerms = args.terms.split(",");
          await Promise.all(
            agreeTerms.map(async (key) => {
              console.log("term agree", key);
              return await dataSources.terms.agree(key);
            })
          );
          await sendEmail(
            signupComplete({ address: args.mailAddress, user: user.user })
          );
          const uid2 = new Uid2Token(req, res);
          await uid2.getToken();
          console.log("############### signup mutation end ###############");
          return { token, user: user.user };
        } catch (e) {
          console.log("signup error", e);
          const response = e?.extensions?.response?.body;
          console.log("idctokensignup error", response);
          if (
            response &&
            response?.error?.code == logicaSignupRestrictionAgeErrorCode
          ) {
            throw new UserInputError(SIGNUP_RESTRICTION_AGE_MESSAGE);
          } else if (
            response &&
            (response?.error?.code == logicaSignupDuplicateErrorCode ||
              response?.error?.code ==
                logicaSignupDuplicateCaseInsensitiveErrorCode)
          ) {
            throw new UserInputError(AUTH_ERROR_MESSAGES["duplicate"]);
          } else if (e instanceof UserInputError) {
            throw e;
          } else {
            throw new UserInputError(AUTH_ERROR_MESSAGES["signup"]);
          }
        }
      },
      idcTokenMapping: async (
        _parent,
        { signature },
        { req, res, dataSources }
      ) => {
        try {
          const idcToken = new IdcToken(req, res);
          const data = await idcToken.verify();
          console.log(data.payload);
          const user = await dataSources.user.idcTokenMapping(
            data.payload.accessToken,
            data.payload.refreshToken
          );
          console.log(user);
          const token = await createToken(req, res, user, signature);
          const uid2 = new Uid2Token(req, res);
          await uid2.getToken();
          return {
            // token,
            user: user.user,
          };
        } catch (e) {
          console.log("idctokenmapping auth error", e);
          // console.log(JSON.stringify(e.extentions, null, 2));
          const response = e?.extensions?.response?.body;
          console.log("idctokenmapping auth error", response);
          if (
            response &&
            response?.error?.code == logicaSignupRestrictionAgeErrorCode
          ) {
            throw new UserInputError(SIGNUP_RESTRICTION_AGE_MESSAGE);
          } else if (
            response &&
            response?.error?.code == logicaAlreadyCooperatedErrorCode
          ) {
            throw new UserInputError(AUTH_ERROR_MESSAGES["cooperated"]);
          } else {
            throw new UserInputError(AUTH_ERROR_MESSAGES["unauthorized"]);
          }
        }
      },
    },
    User: {
      __resolveType(user) {
        switch (user.type) {
          case "account":
            return "AccountUser";
          case "profile":
            return "ProfileUser";
        }
        return "User";
      },
    },
    AccountUser: {
      fullName: (user) => `${user.last_name} ${user.first_name}`,
      currentProfile: async (parent, _args, { req, res, dataSources }) => {
        const userToken = new UserToken(req, res);
        const token = await userToken.verify();
        if (
          (token && token.payload?.currentProfile) ||
          parent.selectProfileId
        ) {
          const profiles = await getProfiles(parent, dataSources);
          const profileId =
            token.payload.currentProfile || parent.selectProfileId;
          const profile = profiles.find((profile) => profile.id == profileId);
          return profile;
        }
        return null;
      },
      profiles: async (parent, _args, { req, dataSources }) => {
        const profiles = await getProfiles(parent, dataSources);
        if (profiles) {
          return profiles.sort((a, b) => a.id - b.id);
        }
        return null;
      },
      test_flg: (user) => values(user, "test_flg"),
      age: (user) => calcAge(user.birthday),
      hashedUserId: (user) => values(user, "user_id_hash"),
      receiveAdvertisingMailType: (user) =>
        values(user, "receive_advertising_mail_type"),
    },
    ProfileUser: {
      passcode: (profile) => values(profile, "passcode"),
      ownerProfile: (profile) => profile?.type == "account" || false,
      needPasscode: (profile) => !!values(profile, "passcode"),
      icon: (profile) => values(profile, "icon"),
      rating: (profile) => values(profile, "rating"),
      age: (user) => calcAge(user.birthday),
    },
    FavoriteMeta: {
      __resolveType: metaResolveType,
    },
  },
  {
    // "Mutation.logout": [isAuthenticated()],
    // "Query.userFavorites": [isAuthenticated()],
    "Query.favoritesByContentType": [isAuthenticated()],
    "Query.viewingHistories": [isAuthenticated()],
    "Mutation.selectProfile": [isAuthenticated()],
    "Mutation.addFavorite": [isAuthenticated()],
    "Mutation.deleteFavorite": [isAuthenticated()],
    "Mutation.updatePassword": [isAuthenticated(), ownerOnly()],
    "Mutation.updateOwnerProfile": [isAuthenticated(), ownerOnly()],
    "Mutation.addProfile": [isAuthenticated(), ownerOnly()],
    "Mutation.updateProfile": [isAuthenticated(), ownerOnly()],
    "Mutation.userDelete": [isAuthenticated(), ownerOnly()],
    "Mutation.advertisingPreferences": [isAuthenticated(), ownerOnly()],
  }
);
